/*
 * lyrics-transcription/pipeline.js — слова песни из трека, целиком в браузере.
 *
 *   файл → декодирование → отделение вокала (Kim Vocal 2, MdxStemSeparator)
 *        → Silero VAD по ВОКАЛЬНОМУ стему → гейт (только спетые куски)
 *        → распознавание (ru: GigaAM, zh / yue: SenseVoice — оба через AsrAdapter;
 *          остальные: whisper-worker)
 *        → строки с таймкодами в координатах исходного файла.
 *
 * Почему именно так — замерено 2026-08-26 (трек клиента #1376 + корпус
 * реальных песен с известным текстом, infra/whisper/BENCHMARK-lyrics-2026-08-26.md):
 *   • на ПОЛНОМ миксе whisper выдумывает («We'll be right back» ×8, «Thank you» ×8)
 *     — крупные модели хуже мелких; после отделения вокала все размеры сходятся;
 *   • от петель декодера («We don't die» ×30) спасают анти-повторы
 *     whisper-worker.js (no_repeat_ngram 6, repetition_penalty 1.15): без них
 *     small на 9 реальных песнях — 113 % WER, с ними — 40 %; резать звук по
 *     VAD НЕ надо — гейт 0.35 поднимает small с 40 до 51 % (тянутые гласные
 *     Silero речью не считает). VAD здесь — только детектор «есть ли пение»;
 *   • VAD по МИКСУ не срабатывает вовсе (0 сегментов при любом пороге) — детектор
 *     работает только по стему;
 *   • GigaAM на русском пении ≈ whisper large-v3 (36–43 % против 33–38 % WER на
 *     двух песнях; эталон сам «плывёт» — hootCer 0.37), при этом отделение вокала
 *     ему не нужно (микс ≈ стем) и петель у CTC не бывает.
 *
 * Китайский — свой движок, и это измерено (2026-09-18, обращение из Тайваня «слова
 * совсем не те»; корпус реальных песен с известным текстом, CER со сведением обоих
 * письменностей к одной — infra/whisper/BENCHMARK-lyrics-cjk-2026-09-18.md):
 *   • путунхуа, 10 песен: whisper-small q8 (то, что шло в браузере) 49.2 % и 448
 *     вставок; whisper large-v3 на видеокарте 32.4 %; SenseVoice int8 — 22.7 % и 44
 *     вставки. Кантонский, 6 песен: 45.8 % → 15.0 % (у whisper-small кантонского токена
 *     нет, он декодирует его как «китайский»);
 *   • причина не в настройке: в пении ТОНЫ путунхуа уходят в мелодию, и омофоны
 *     (丘陵/秋林, 盆地/潘地) различает только языковая модель, а у whisper-small она для
 *     китайского слабая. Плюс авторегрессионный декодер СОЧИНЯЕТ текст на проигрыше
 *     («Zither Harp», фальшивые титры «詞:陳宇 曲:陳宇», «由 Amara.org 社群提供的字幕») —
 *     у CTC сочинять нечем;
 *   • отделение вокала CTC-движкам ПОМОГАЕТ (SenseVoice 24.4 → 20.2 % на тех же
 *     песнях, GigaAM 34.8 → 31.0), whisper — нет. Поэтому «Точнее» отделяет вокал у
 *     русского и китайского лейнов, а у whisper означает модель покрупнее;
 *   • японский и корейский ОСТАЮТСЯ на whisper: 28.6 % против 33.3 % у исходного
 *     SenseVoice на японском, корейский — вничью; а тот экспорт SenseVoice, что хорош
 *     на китайском, японский и корейский превращает в иероглифическую кашу.
 *
 * Контракт: LyricsPipeline.run(file, opts) → Promise<result>, см. run() ниже.
 * Чистые функции (гейт, сборка строк, чистка) вынесены и экспортированы в
 * _internal — их гоняет tests/node/lyrics-pipeline.test.mjs.
 */
(function (global) {
    'use strict';

    var SR = 16000;

    /* Имя языка для whisper-worker (transformers.js ждёт английское название). */
    var WHISPER_LANG = {
        en: 'english', ru: 'russian', ko: 'korean', ar: 'arabic', id: 'indonesian',
        pt: 'portuguese', de: 'german', es: 'spanish', fr: 'french', it: 'italian',
        ja: 'japanese', zh: 'chinese', yue: 'chinese', tr: 'turkish', uk: 'ukrainian', pl: 'polish',
        nl: 'dutch', hi: 'hindi', sv: 'swedish', cs: 'czech', ro: 'romanian',
        el: 'greek', he: 'hebrew', th: 'thai', vi: 'vietnamese', fi: 'finnish',
        da: 'danish', nb: 'norwegian', hu: 'hungarian', bg: 'bulgarian', ms: 'malay'
    };

    /* Фразы, которые whisper сочиняет на тишине и проигрышах. Список — из
       реальных выдач этого замера (en/ru) плюс те же «титры» на языках сайта. */
    var HALLUC = [
        /thank(s| you) for (watching|listening)/i, /we'?ll be right back/i,
        /see you (in the )?next (time|video|one)/i, /\bsubscribe\b/i,
        /^\s*(thank you[.!]?\s*)+$/i, /^\s*(bye[.!]?\s*)+$/i,
        /спасибо за (просмотр|внимание)/i, /подписывайтесь/i, /продолжение следует/i,
        /редактор субтитров/i, /субтитры (сделал|подготовил|создавал)/i,
        /gracias por ver/i, /merci d['’]avoir regard/i, /obrigad[oa] por assistir/i,
        /danke f(ü|u)rs? zuschauen/i, /untertitel(ung)? (von|im auftrag)/i,
        /terima kasih (telah|sudah) menonton/i, /시청해 ?주셔서 감사합니다/, /구독/,
        /شكرا (على|لك على) المشاهدة/,
        /* Китайский whisper на проигрыше (замер 2026-09-18, и small, и large-v3): */
        /zither\s*harp/i, /amara\.?\s*org/i, /优优独播剧场|yoyo television series/i,
        /字幕志[愿願]者/, /[请請]不吝[点點][赞贊]/,
        /^\s*[\(（]?\s*(作[词詞]作?曲?|作曲|[编編]曲|作[词詞][编編]曲|作[词詞]曲)\s+\S{1,8}\s*[\)）]?\s*$/
    ];
    /* Фальшивые титры («詞:林宇 曲:林宇 編曲:林宇 混音:林宇») whisper вставляет и ВНУТРЬ
       настоящей строки («情绪堆积半余字幕:J.Y. 曲:J.Y.») — поэтому вырезаются, а не
       выбрасываются вместе со строкой. С двоеточием в песнях так не пишут. */
    var CREDIT_LABEL = '(?:作?[词詞]|作?曲|[编編]曲|混音|母[带帶帖]|和[声聲]|吉他|字幕|[监監][制製]|[制製]作人?)\\s*[:：]';
    /* Имя — лениво и до ближайшего конца: пробел, скобка, конец строки или СЛЕДУЮЩИЙ титр.
       Жадное имя съедало соседний ярлык: «曲:陳宇霍編曲:陳宇堂» оставляло «:陳宇堂». */
    var CREDIT_RE = new RegExp('[\\(（]?\\s*' + CREDIT_LABEL + '\\s*[^\\s()（）:：]{1,10}?(?=\\s|$|[()（）]|' + CREDIT_LABEL + ')\\s*[\\)）]?', 'g');
    /* Служебные пометки, которые модель пишет вместо слов: [музыка], (upbeat music), ♪ */
    var TAG_RE = /\[[^\]]*\]|\([^)]*\)|\*[^*\n]{1,40}\*|♪|\uFFFD/g;   // [музыка] (upbeat music) *Musik* ♪

    function clean(text) {
        return String(text || '').replace(CREDIT_RE, ' ').replace(TAG_RE, ' ').replace(/\s+/g, ' ').trim();
    }
    function isHalluc(text) {
        var t = String(text || '');
        for (var i = 0; i < HALLUC.length; i++) if (HALLUC[i].test(t)) return true;
        return false;
    }
    /* Доля слов латиницей в русской выдаче. GigaAM — модель РУССКОГО языка: на
       чужой песне она транслитерирует («ser down netu», «That o Drims cane tw»),
       и это видно по алфавиту. Замерено 27.08 на реальных выдачах:
           настоящие русские песни  0.0 / 0.0 / 0.6 / 8.1 %
           английская песня         93.2 % (сервер) и 98.9 % (через тул)
       Порог 0.3 лежит в пустоте между популяциями, а не «на глаз». */
    function latinShare(text) {
        var words = String(text || '').match(/[A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё']*/g);
        if (!words || !words.length) return 0;
        var lat = 0;
        for (var i = 0; i < words.length; i++) if (/^[A-Za-z]/.test(words[i])) lat++;
        return lat / words.length;
    }
    var FOREIGN_LATIN_SHARE = 0.3;

    /* Экономия трафика: у GigaAM своя модель на 225 МБ, и на помеченной
       «экономной» сети мы её не тянем — остаёмся на уже загруженном whisper. */
    function isSaveData() {
        try { return !!(navigator.connection && navigator.connection.saveData); } catch (e) { return false; }
    }

    function aborted(signal) { return !!(signal && signal.aborted); }
    /* Погашенный отказ воркера (см. обработчики ниже) не должен пропасть из виду:
       window.onerror его больше не видит, поэтому он уходит диагностикой. */
    function diag(issue, ev) {
        try { if (global.__aeDiagnostic) global.__aeDiagnostic({ issue: issue, msg: String((ev && ev.message) || '').slice(0, 120) }); } catch (e) { /* noop */ }
    }
    function abortError() { var e = new Error('aborted'); e.name = 'AbortError'; return e; }

    /* ── декодирование ───────────────────────────────────────────────────── */
    /* Через общий AudioDecoderRobust (CLAUDE.md, «Декодировать ЗВУК из файла»): голый
       decodeAudioData не открывает видео с iPhone в Safari — 30 дней до 17.10.2026 это
       была самая частая ошибка тула («Decoding failed», 59 из 69 — .mov в Safari), а
       примитив снимает звук mp4-семейства через WebCodecs и дальше по ступеням.
       Свой путь остаётся только для страницы, где примитив не загрузился. */
    function decodeFile(file, signal) {
        var R = global.AudioDecoderRobust;
        if (R && typeof R.robust === 'function' && !R.__isStub) {
            var Ctx0 = global.AudioContext || global.webkitAudioContext;
            var ctx0 = null;
            try { ctx0 = new Ctx0(); } catch (e) { /* примитив создаст свой */ }
            var close0 = function () { try { if (ctx0) ctx0.close(); } catch (e) { /* noop */ } };
            return R.robust(file, ctx0, { slug: 'lyrics-transcription', signal: signal }).then(function (r) {
                close0();
                return r.buffer;
            }, function (err) { close0(); throw err; });
        }
        return file.arrayBuffer().then(function (ab) {
            var Ctx = global.AudioContext || global.webkitAudioContext;
            var ctx = new Ctx();
            return new Promise(function (resolve, reject) {
                var p = ctx.decodeAudioData(ab, resolve, reject);
                if (p && p.then) p.then(resolve, reject);
            }).then(function (buf) {
                try { ctx.close(); } catch (e) { /* noop */ }
                return buf;
            }, function (err) {
                try { ctx.close(); } catch (e) { /* noop */ }
                throw err;
            });
        });
    }

    /* AudioBuffer (любая частота, любое число каналов) → моно 16 кГц Float32. */
    function toMono16k(buf) {
        var len = Math.max(1, Math.ceil(buf.duration * SR));
        var Off = global.OfflineAudioContext || global.webkitOfflineAudioContext;
        var off = new Off(1, len, SR);
        var src = off.createBufferSource();
        src.buffer = buf;
        src.connect(off.destination);
        src.start(0);
        return off.startRendering().then(function (rendered) {
            return rendered.getChannelData(0).slice();
        });
    }

    /* ── VAD ─────────────────────────────────────────────────────────────── */
    /* Silero по вокальному стему. Отказ воркера → null: без гейта хуже, чем без
       ничего, но не смертельно; вызывающий решает, что делать. */
    function runVad(pcm, url, options, onProgress, signal) {
        return new Promise(function (resolve) {
            var w = null, done = false;
            function finish(v) {
                if (done) return; done = true;
                try { if (w) w.terminate(); } catch (e) { /* noop */ }
                resolve(v);
            }
            try { w = new Worker(url); } catch (e) { return resolve(null); }
            var to = setTimeout(function () { finish(null); }, 240000);
            w.onmessage = function (ev) {
                var m = ev.data || {};
                if (m.type === 'result') { clearTimeout(to); finish(m); }
                else if (m.type === 'error') { clearTimeout(to); finish(null); }
                else if (m.type === 'progress' && onProgress) onProgress((+m.progress || 0) / 100);
            };
            /* Отказ воркера обработан (null выше по течению), поэтому событие гасится:
               непогашенное уходит в window.onerror — общий тост «что-то пошло не так»
               поверх продолжающегося прогона, телеметрия и Sentry. */
            w.onerror = function (ev) { if (ev && ev.preventDefault) ev.preventDefault(); clearTimeout(to); diag('lyrics_vad_worker_died', ev); finish(null); };
            if (signal) signal.addEventListener('abort', function () { clearTimeout(to); finish(null); });
            var copy = new Float32Array(pcm);
            w.postMessage({ type: 'detect', audioBuffer: copy.buffer, options: options || {} }, [copy.buffer]);
        });
    }

    /* ── гейт ────────────────────────────────────────────────────────────── */
    /* Склеивает только спетые сегменты (с запасом pad с обеих сторон) через паузу
       gap секунд тишины и запоминает карту «время в склейке → время в файле».
       Пауза нужна декодеру как граница фразы; без неё соседние строки слипаются. */
    function buildGate(pcm, segments, opts) {
        opts = opts || {};
        var pad = opts.padSec != null ? opts.padSec : 0.25;
        var gap = opts.gapSec != null ? opts.gapSec : 0.4;
        var total = pcm.length / SR;
        var segs = (segments || []).map(function (s) {
            return { start: Math.max(0, s.start - pad), end: Math.min(total, s.end + pad) };
        }).filter(function (s) { return s.end > s.start; }).sort(function (a, b) { return a.start - b.start; });
        // сегменты, сросшиеся после расширения, — в один
        var merged = [];
        segs.forEach(function (s) {
            var last = merged[merged.length - 1];
            if (last && s.start <= last.end) last.end = Math.max(last.end, s.end);
            else merged.push({ start: s.start, end: s.end });
        });
        var gapN = Math.round(gap * SR), n = 0;
        merged.forEach(function (s) { n += Math.round((s.end - s.start) * SR) + gapN; });
        var out = new Float32Array(Math.max(1, n)), pos = 0, map = [];
        merged.forEach(function (s) {
            var a = Math.round(s.start * SR), b = Math.min(pcm.length, Math.round(s.end * SR));
            out.set(pcm.subarray(a, b), pos);
            map.push({ g0: pos / SR, g1: (pos + (b - a)) / SR, o0: s.start });
            pos += (b - a) + gapN;
        });
        var speech = merged.reduce(function (acc, s) { return acc + (s.end - s.start); }, 0);
        return { pcm: out, map: map, speechSec: speech, segments: merged };
    }
    /* Время в склейке → время в исходном файле. Внутри паузы — конец
       предыдущего куска (модель иногда «доводит» слово в тишину). */
    function origTime(map, t) {
        if (!map || !map.length) return t;
        for (var i = 0; i < map.length; i++) {
            var m = map[i];
            if (t < m.g0) return i === 0 ? m.o0 : map[i - 1].o0 + (map[i - 1].g1 - map[i - 1].g0);
            if (t <= m.g1) return m.o0 + (t - m.g0);
        }
        var last = map[map.length - 1];
        return last.o0 + (last.g1 - last.g0);
    }

    /* ── whisper: один воркер на СТРАНИЦУ, а не на прогон ─────────────────────
       Замер 17.10.2026 (30 дней телеметрии, обращение «11 % и всё»): этап «Определяю
       язык» — главное место, где тул теряет людей. 271 сессия из 1089, дошедших до него,
       дальше не прошла; почти все — телефоны, ждавшие 10–60 с при неподвижной полосе,
       70 из них перезапускали. А под этим этапом молча качается модель (base — 77 МБ),
       и каждый перезапуск начинал её С НУЛЯ: воркер жил столько же, сколько прогон.
       Заявитель на канале ≈ 250 КБ/с запускал трижды (118 с, 100 с, 300 с) и каждый раз
       терял скачанное — третий раз за пару мегабайт до конца, когда сработал срок.

       Поэтому воркер принадлежит странице: «Отмена» и новый «Старт» продолжают ту же
       загрузку; распознавание той же моделью идёт в том же воркере (без второго создания
       сессии и без второго выделения памяти — на Android с 2 ГБ второе выделение 23 МБ
       под энкодер и падало: «failed to allocate a buffer of size 23201314», 12 раз за
       30 дней, все на этапе модели). Другая модель — свежий воркер, как и раньше: смена
       модели внутри воркера не замерялась.

       Задания на воркере — строго по одному: его ответы не несут номера задания, а
       ORT-сессия не принимает два вывода разом. Отменённый прогон снимается со
       страницы сразу, а его задание на воркере доживает до ответа — следующее ждёт за
       ним (определение языка — секунды; распознавание при отмене воркер гасит). */
    function openWhisper(url) {
        try { return new Worker(url, { type: 'module' }); } catch (e) { return null; }
    }
    var held = null;   // { w, url, model, tail, dead, drops[] }
    function holdWhisper(url, model) {
        if (held && !held.dead && held.url === url && held.model === model) return held;
        dropWhisper();
        var w = openWhisper(url);
        if (!w) return null;
        held = { w: w, url: url, model: model, tail: Promise.resolve(), dead: false, drops: [] };
        return held;
    }
    function dropWhisper() {
        var h = held;
        held = null;
        if (!h || h.dead) return;
        h.dead = true;
        try { h.w.terminate(); } catch (e) { /* noop */ }
        /* Ожидающие ответа этого воркера его уже не получат — отпускаем их. */
        h.drops.splice(0).forEach(function (fn) { try { fn(); } catch (e) { /* noop */ } });
    }
    /* job(settle) должен вызвать settle(value) ровно тогда, когда воркер ответил на это
       задание (или умер); вызывающему возвращается промис этого ответа. */
    function enqueue(h, job) {
        var run = h.tail.then(function () {
            return new Promise(function (resolve) {
                if (h.dead) return resolve({ dropped: true });
                var done = false;
                function settle(v) {
                    if (done) return; done = true;
                    var i = h.drops.indexOf(onDrop); if (i >= 0) h.drops.splice(i, 1);
                    resolve(v);
                }
                function onDrop() { settle({ dropped: true }); }
                h.drops.push(onDrop);
                job(settle);
            });
        });
        h.tail = run.then(function () {}, function () {});
        return run;
    }
    /* Сторож тишины воркера — по ВИДИМОМУ времени (CLAUDE.md, «сторож воркер молчит
       N с»): скрытую вкладку телефон замораживает, и стенные часы объявили бы мёртвым
       воркер, который просто ждал человека. */
    function after(fn, ms) {
        if (typeof global.__visibleTimeout === 'function') return global.__visibleTimeout(fn, ms);
        var t = setTimeout(fn, ms);
        return function () { clearTimeout(t); };
    }
    /* До 17.10 здесь стоял СРОК: 300 с на всё определение языка, включая скачивание
       модели. Он убивал загрузку, которая шла, — на медленном канале 77 МБ дольше пяти
       минут — и прогон уходил дальше без языка, начиная качать следующую модель. Сторож
       теперь смотрит только на МОЛЧАНИЕ: любое сообщение воркера (байты загрузки,
       готовый отрывок) его перезаводит. 150 с тишины — с запасом: после скачивания
       воркер создаёт сессию ORT без сообщений (на ноутбуке 1–2 с), отрывок на base —
       секунды; свои стопоры загрузки у воркера короче (20 с без первых байтов). */
    var WORKER_SILENCE_MS = 150000;

    /* ── Определение языка ───────────────────────────────────────────────
       transformers.js язык НЕ определяет: без `language` он подставляет
       английский и ПЕРЕВОДИТ (замер 27.08: русская песня вернулась строкой
       "I have been looking for you for years"), `return_language` отдаёт null,
       `output_scores` игнорируется. Рабочий путь — прямой проход модели в
       воркере (detect_language), здесь — выбор отрывков и разбор ответа.
       Замер на 9 реальных песнях: base × 4 отрывка 8/9, tiny × 4 и один
       отрывок — 7/9. */
    /* Середина песни — первой: там поют почти всегда, а у начала и конца бывают
       вступление и проигрыш. Порядок важен только для раннего выхода (сумма четырёх от
       порядка не зависит): воркер останавливается, когда ДВА первых отрывка согласны с
       p ≥ 0.6. Замер 17.10.2026 на 49 реальных песнях 12 языков (base, WASM): остановка
       после двух — на 30 из 49, ни одного изменённого решения, 46/49 верно и так и так;
       при исходном порядке — 28 из 49. */
    var LID_SLICES = [0.35, 0.55, 0.15, 0.75];
    var LID_EARLY = { after: 2, minP: 0.6 };
    /* Код языка у whisper ≠ код в списке тула: норвежский у whisper — `no`, в списке
       (config/lyrics-transcription.php) — `nb`. Без перевода фильтр по списку выбрасывал
       верный ответ, и норвежская песня на «Авто» уходила к следующему кандидату.
       Индонезийское пение whisper метит ЯВАНСКИМ (`jw`), которого в списке нет, и фильтр
       отдавал следующего кандидата — корейский у base, малайский у small. Замер 17.10.2026
       на 49 песнях: `jw → id` — base 46 → 47/49, small 46 → 48/49; ни у одной
       неиндонезийской песни `jw` не стоял первым. */
    var LID_ALIAS = { no: 'nb', jw: 'id' };
    var LID_SEC = 30;

    function sliceAt(pcm, frac) {
        var n = Math.min(pcm.length, Math.round(LID_SEC * SR));
        var at = Math.max(0, Math.min(pcm.length - n, Math.round(pcm.length * frac)));
        return pcm.slice(at, at + n);
    }

    /* → { lang, p, top:[{lang,p}], loadMs, computeMs } либо null. `allow` — коды
       языков тула: без ограничения побеждают соседи, которых мы не предлагаем (замер:
       индонезийская песня уходит в яванский). `h` — удерживаемый воркер (holdWhisper).
       hooks.onModel(frac, loaded, total) — скачивание модели, hooks.onLid(frac) —
       готовые отрывки: до 17.10 этап молчал всё это время, и полоса стояла. */
    function detectLang(h, pcm, params, allow, signal, hooks) {
        hooks = hooks || {};
        function live() { return !aborted(signal); }
        var slices = LID_SLICES.map(function (f) { return sliceAt(pcm, f); });
        var answer = !h ? Promise.resolve(null) : enqueue(h, function (settle) {
            var w = h.w, stopWatch = null;
            function finish(v) {
                if (stopWatch) stopWatch();
                w.removeEventListener('message', onMsg); w.removeEventListener('error', onErr);
                settle(v);
            }
            function watch() {
                if (stopWatch) stopWatch();
                stopWatch = after(function () {
                    diag('lyrics_lid_worker_silent', { message: 'no message for ' + Math.round(WORKER_SILENCE_MS / 1000) + 's' });
                    finish(null);
                    if (held === h) dropWhisper();       // молчащий воркер следующему прогону не годится
                }, WORKER_SILENCE_MS);
            }
            /* Воркер, умерший ДО ответа (не загрузился его скрипт), сообщения не пришлёт
               никогда — без этого слушателя определение языка ждало до срока. Отказ
               обработан (язык страницы), поэтому событие гасится. */
            function onErr(ev) {
                if (ev && ev.preventDefault) ev.preventDefault();
                diag('lyrics_lid_worker_died', ev);
                finish(null);
                if (held === h) dropWhisper();
            }
            function onMsg(ev) {
                var m = ev.data || {};
                watch();
                if (m.type === 'language_detected') finish({ m: m });
                else if (m.type === 'language_detect_failed') finish(null);
                else if (m.type === 'lid_progress' && live() && hooks.onLid && m.total) hooks.onLid(m.done / m.total);
            }
            w.addEventListener('error', onErr);
            w.addEventListener('message', onMsg);
            watch();
            var bufs = slices.map(function (x) { return x.buffer; });
            w.postMessage({ type: 'detect_language', modelSize: params.modelSize, forceWasm: !!params.forceWasm, slices: bufs,
                            earlyStop: { after: LID_EARLY.after, minP: LID_EARLY.minP, allow: allow || null } }, bufs);
        });
        /* Загрузка модели — общая для воркера, а не для задания: после «Отмены» новый
           прогон стоит в очереди ЗА старым заданием, пока оно докачивает ту же модель, и
           слушай он только своё задание — полоса снова стояла бы ровно в сценарии
           заявителя. Поэтому байты загрузки слушаются сразу. */
        function onLoad(ev) {
            var m = ev.data || {};
            if (!live() || !hooks.onModel) return;
            if (m.type === 'download_progress') {
                var d = m.data || {};
                hooks.onModel((+d.progress || 0) / 100, d.loaded, d.total);
            } else if (m.type === 'backend') hooks.onModel(1);
        }
        if (h) h.w.addEventListener('message', onLoad);
        /* Отмена снимает ожидание СТРАНИЦЫ сразу; задание на воркере доживает (см. выше). */
        return new Promise(function (resolve) {
            var out = function (v) { if (h) h.w.removeEventListener('message', onLoad); resolve(v); };
            if (signal) signal.addEventListener('abort', function () { out(null); });
            answer.then(function (v) {
                var m = v && v.m;
                if (!m) return out(null);
                var top = (m.top || []).map(function (x) { return LID_ALIAS[x.lang] ? { lang: LID_ALIAS[x.lang], p: x.p } : x; })
                    .filter(function (x) { return !allow || allow.indexOf(x.lang) >= 0; });
                out(top.length ? { lang: top[0].lang, p: top[0].p, top: top, loadMs: m.loadMs, computeMs: m.computeMs } : null);
            }, function () { out(null); });
        });
    }

    function whisperRun(h, pcm, params, hooks, signal) {
        if (!h) return Promise.reject(new Error('worker unavailable'));
        /* Отмена распознавания гасит воркер: это минуты процессора, и следующему прогону
           ждать их за собой незачем (в отличие от загрузки модели, которую он продолжит). */
        var onAbort = function () { if (held === h) dropWhisper(); };
        if (signal) signal.addEventListener('abort', onAbort);
        return enqueue(h, function (settle) {
            if (aborted(signal)) return settle({ err: abortError() });
            whisperJob(h.w, pcm, params, hooks, signal, settle);
        }).then(function (v) {
            if (signal) signal.removeEventListener('abort', onAbort);
            if (aborted(signal)) throw abortError();
            if (!v || v.dropped) throw new Error('worker dropped');
            if (v.err) { if (held === h) dropWhisper(); throw v.err; }
            return v.ok;
        });
    }
    /* Одно задание распознавания на воркере; settle({ok}) / settle({err}) — ответ. */
    function whisperJob(w, pcm, params, hooks, signal, settle) {
        var settled = false;
        function finish(v) {
            if (settled) return; settled = true;
            w.removeEventListener('message', onMsg);
            w.removeEventListener('error', onErr);
            settle(v);
        }
        /* Прогресс распознавания: между «модель скачана» и первым
           transcribe_progress проходит до минуты (создание сессии WebGPU,
           первое окно), и без сердцебиения полоса выглядит зависшей — ровно
           так это и выглядело на проде 27.08 («остановилось на 72 %»).
           Воркер шлёт status/heartbeat/partial_text/chunk_done — двигаем
           полосу по ним понемногу, реальный audioTime её только обгоняет. */
        var lastFrac = 0;
        function bump(frac) { if (frac > lastFrac) { lastFrac = frac; if (hooks.onProgress && !aborted(signal)) hooks.onProgress(frac); } }
        function onMsg(ev) {
            var m = ev.data || {};
            if (m.type === 'result') finish({ ok: m.result || {} });
            else if (m.type === 'error') finish({ err: new Error(m.message || 'whisper error') });
            else if (m.type === 'download_progress') {
                var d = m.data || {};
                if (hooks.onModel && !aborted(signal)) hooks.onModel((+d.progress || 0) / 100, d.loaded, d.total);
            }
            /* «loading_model» — это НАЧАЛО загрузки. До 17.10 на нём ставилось
               onModel(1): полоса прыгала в конец этапа модели и, раз она ходит только
               вперёд, всё скачивание small (249 МБ) стояла на месте. Готовность модели —
               сообщение `backend`. */
            else if (m.type === 'status') {
                if (m.status === 'transcribing') bump(0.001);
                else if (m.status === 'loading_model' && hooks.onModel && !aborted(signal)) hooks.onModel(0);
            }
            else if (m.type === 'transcribe_progress') bump(Math.min(1, (+m.audioTime || 0) / (pcm.length / SR)));
            else if (m.type === 'webgpu_fallback') { if (hooks.onNote) hooks.onNote('webgpu_fallback', m); bump(Math.min(0.97, lastFrac + 0.004)); }
            else if (m.type === 'backend') { if (hooks.onModel && !aborted(signal)) hooks.onModel(1); bump(Math.min(0.97, lastFrac + 0.004)); }
            else if (m.type === 'heartbeat' || m.type === 'partial_text' || m.type === 'chunk_done') bump(Math.min(0.97, lastFrac + 0.004));
            else if (m.type === 'model_downgraded') { bump(0.001); if (hooks.onNote) hooks.onNote('model_downgraded', m); }
        }
        /* Отказ уходит в ответ задания — его говорит страница; непогашенное событие
           добавило бы к её фразе общий тост из window.onerror. */
        function onErr(err) { if (err && err.preventDefault) err.preventDefault(); finish({ err: new Error((err && err.message) || 'worker error') }); }
        w.addEventListener('message', onMsg);
        w.addEventListener('error', onErr);
        var copy = new Float32Array(pcm);
        var payload = {
            type: 'transcribe', audioBuffer: copy.buffer, modelSize: params.modelSize || 'small',
            mediaSec: Math.round(copy.length / SR), vadUsed: !!params.vadUsed,
            /* Устройство, у которого WebGPU уже отдавал вырожденный текст, не
               должно платить за это второй раз: воркер тогда прогоняет ВСЁ
               заново на WASM (замер 27.08: +140 с на песню). Страница помнит
               флаг между запусками — как в /speech-to-text. */
            /* ⚠️⚠️ ПЕНИЕ НЕ ПУСКАЕМ НА WebGPU ВООБЩЕ — это не «иногда
               хуже», а не работает. Замер 28.08 на машине Фарида, один и
               тот же 120-секундный отрывок вокального стема, whisper-small,
               штраф 1.15 (отгружаемая конфигурация):

                   язык | wasm q8                    | webgpu fp16
                   en   | 139 слов настоящего текста | 92 «слова», из них 26
                        |                            | подряд [Music]/♪, дальше выдумка
                   es   | 75                         | 6
                   fr   | 111                        | 27 (сплошь «>>» и обрывки)
                   ru   | 57                         | 0

               ⚠️ Стенд принудительно ставил language:'english' — обе ветви
               в равных условиях, так что сравнение бэкендов честное, но
               сами тексты нерусских строк не равны выдаче тула. Решающее
               свидетельство с правильным языком — три сквозных прогона
               полной песни: 90.1 % на fp16 против 16.7–17.9 % на wasm.

               На полной песне fp16 без штрафа срывается в чистую петлю:
               три прогона дали побайтово одинаковые 90.1 % WER. WebGPU+q8
               ещё хуже — многоязычная каша и ВДЕСЯТЕРО медленнее (1443 с
               против 122). Цена: WASM в 2–4 раза медленнее на самом
               распознавании, но она окупается отказом от отделения вокала
               ниже (150–290 с), которое на этой же конфигурации не помогает.
               ⚠️ Речевые тулы этим НЕ трогаются: у них WebGPU замерен как
               полезный. Здесь общий сторож (asr-quality-guard) — лишь
               страховка: чистую петлю он ловит, а подмену вокала значками
               [Music] (уникальность 0.60, выше порога) — нет. */
            forceWasm: true,
            /* ⚠️⚠️ Штраф за повтор НЕ трогаем — здесь стояло его выключение,
               и оно было ошибкой измерения. Развёртка 28.08 сперва гоняла
               декодер с `return_timestamps: false`, а тул распознаёт С
               ТАЙМКОДАМИ (иначе нет ни LRC, ни SRT) — это другой путь
               декодирования. На той фикстуре выключение выглядело выигрышем
               (35.8 против 43.3 %). Повтор той же развёртки с таймкодами,
               те же девять песен и те же q8-байты:
                   штраф 1.15 (дефолт воркера) — 50.2 % WER
                   штраф 1.0  (выключен)       — 52.9 %, причём арабская
                                                 песня уходит в разнос: 86 → 130 %
               Вывод сильнее самого числа: фикстура, которая не
               воспроизводит РАБОЧУЮ конфигурацию тула, ранжирует варианты
               в другом порядке — и «улучшение» на ней ухудшает тул. */
        };
        if (params.language) payload.language = params.language;
        if (params.initialPrompt) payload.initialPrompt = params.initialPrompt;
        w.postMessage(payload, [copy.buffer]);
    }

    /* ── CTC-движки: GigaAM (русский), SenseVoice (китайский) ─────────────────
       Один путь на оба: у воркеров общий протокол, разницу держит AsrAdapter.
       `adapterCfg` — то, что ждёт AsrAdapter.create: { urls: … }. */
    function ctcRun(lang, adapterCfg, pcm, hooks, signal) {
        if (!global.AsrAdapter) return Promise.reject(new Error('AsrAdapter missing'));
        var job = 1;
        var adapter = global.AsrAdapter.create(lang, adapterCfg, {
            onModelProgress: function (loaded, total) { if (hooks.onModel && total) hooks.onModel(loaded / total); },
            onModelReady: function () { if (hooks.onModel) hooks.onModel(1); },
            onProgress: function (doneS, totalS) { if (hooks.onProgress && totalS) hooks.onProgress(doneS / totalS); },
            isStale: function () { return aborted(signal); }
        });
        if (signal) signal.addEventListener('abort', function () { try { adapter.abort(); } catch (e) { /* noop */ } });
        // go() ДО init(): воркер с deferSession создаёт ORT-сессию только после
        // init_go, а init() резолвится на init_done — ждать init() перед go()
        // значит ждать вечно (первый прогон так и повис на «Загружаю модель»).
        adapter.go();
        return adapter.init().then(function () {
            return adapter.transcribe(new Float32Array(pcm), job, {});
        }).then(function (res) {
            try { adapter.terminate(); } catch (e) { /* noop */ }
            return res;
        }, function (err) {
            try { adapter.terminate(); } catch (e) { /* noop */ }
            throw err;
        });
    }

    /* Куски whisper → строки. Пустой таймкод (`[null, …]` у ПОСЛЕДНЕГО куска —
       так whisper закрывает поток) продолжает предыдущую строку, а не падает в
       ноль: иначе последняя строка уезжала в начало с временем 0:00 и текст
       выглядел оборванным (замер 27.08 на «Someone Like You»: строка 36 из 37
       имела 0:00 при соседних 4:23). Время, откатившееся назад, тоже
       подтягивается вперёд — декодер иногда сбрасывает счётчик после
       восстановления. */
    function chunksToLines(chunks, map) {
        var prevEnd = 0;
        return (chunks || []).map(function (c) {
            var ts = c.timestamp || [];
            var s = (ts[0] == null) ? prevEnd : +ts[0];
            if (!isFinite(s) || s < prevEnd - 0.5) s = prevEnd;
            var e = (ts[1] == null) ? s + 2 : +ts[1];
            if (!isFinite(e) || e < s) e = s + 2;
            prevEnd = e;
            return { s: origTime(map, s), e: origTime(map, e), text: c.text };
        });
    }

    /* Кусок OpenAI (~12 с, текст без слов-таймингов) → строки: по концу фразы,
       длинная фраза без точек — ещё и по запятым; время куска делится между
       строками по длине текста. Без lookbehind в регулярках: старый Safari не
       разобрал бы весь файл. */
    function piecesToLines(segs) {
        var out = [];
        (segs || []).forEach(function (sg) {
            var parts = (String(sg.text || '').match(/[^.!?…。！？\n]+[.!?…。！？]*/g) || [])
                .map(function (t) { return t.trim(); }).filter(Boolean);
            var fine = [];
            parts.forEach(function (t) {
                if (t.length > 70 && t.indexOf(',') > 0) {
                    (t.match(/[^,]+,?/g) || [t]).forEach(function (x) { if (x.trim()) fine.push(x.trim()); });
                } else fine.push(t);
            });
            var total = 0;
            fine.forEach(function (t) { total += t.length; });
            var at = +sg.start || 0, span = Math.max(0.2, (+sg.end || 0) - at);
            fine.forEach(function (t) {
                var d = span * t.length / (total || 1);
                out.push({ s: at, e: at + d, text: t });
                at += d;
            });
        });
        return out;
    }

    /* Слова с таймингами → строки: новая строка на паузе ≥ gap с или после
       maxWords слов (длинная строка без пауз — обычно припев).
       Единицы китайского лейна — отдельные иероглифы с `space:false`: они склеиваются
       БЕЗ пробела, а длину строки держит maxChars (у строки без пробелов счёт «слов»
       ничего не значит). У слов без поля `space` (GigaAM) поведение прежнее. */
    function wordsToLines(words, opts) {
        opts = opts || {};
        var gap = opts.gapSec != null ? opts.gapSec : 0.8;
        var maxWords = opts.maxWords || 12;
        var maxChars = opts.maxChars || 0;
        var lines = [], cur = null;
        (words || []).forEach(function (w) {
            var text = clean(w.text);
            if (!text) return;
            var glue = (w.space === false) ? '' : ' ';
            var over = cur && (maxChars ? (glue === '' && cur.chars >= maxChars) || (glue !== '' && cur.words >= maxWords)
                                        : cur.words >= maxWords);
            if (cur && (w.start - cur.e >= gap || over)) { lines.push(cur); cur = null; }
            if (!cur) cur = { s: w.start, e: w.end, text: text, words: 1, chars: text.length, unspaced: glue === '' };
            else { cur.text += glue + text; cur.e = w.end; cur.words++; cur.chars += text.length; cur.unspaced = cur.unspaced && glue === ''; }
        });
        if (cur) lines.push(cur);
        /* Сироты. В пении иероглиф выходит на АТАКЕ слога, и тянутая нота выглядит паузой:
           последний знак фразы оставался один на строке («…頂部有夠平 / 坦»). Строка короче
           minChars приклеивается к предыдущей, если началась не позже glueSec после неё. */
        if (opts.minChars > 1) {
            var glued = [];
            lines.forEach(function (l) {
                var prev = glued[glued.length - 1];
                if (prev && l.chars < opts.minChars && l.s - prev.e < (opts.glueSec || 0)) {
                    prev.text += (l.unspaced && prev.unspaced ? '' : ' ') + l.text;
                    prev.e = l.e; prev.words += l.words; prev.chars += l.chars;
                } else glued.push(l);
            });
            lines = glued;
        }
        return lines.map(function (l) { return { s: l.s, e: l.e, text: l.text }; });
    }

    /* Строки китайского лейна — из замера, а не «на глаз» (tests/asr-bench/lyrics-cjk/
       line_breaks2.py: распознанные иероглифы выровнены с эталонным текстом, 13 песен,
       F1 по границам ПЕЧАТНЫХ строк):
           пауза ≥ 0.6 с — 0.657 · ≥ 0.7 — 0.679 · ≥ 0.8 — 0.713 (≈41 строка на песню)
       Потолок длины ВРЕДИТ при любой паузе (0.8 с: без потолка 0.713, 20 знаков 0.696,
       14 — 0.669, 10 — 0.601): он рвёт строку посреди фразы. Поэтому maxChars здесь —
       только страховка от речитатива без единой паузы; настоящая спетая фраза — это
       6 знаков в медиане и 15 в максимуме, до 28 она не дотягивается.
       Сироты (line_breaks3.py, те же 13 песен): без склейки F1 0.713 и 5.5 строк в 1–2 знака
       на песню; «короче 3 знаков и не позже 2.5 с» — F1 0.722 и 0.3 такой строки. */
    var CJK_LINE = { gapSec: 0.8, maxChars: 28, minChars: 3, glueSec: 2.5 };

    /* Чистка строк: пометки, выдумки, пустое, повтор подряд той же строки
       (декодер иногда отдаёт одну фразу дважды на границе окна). */
    function postLines(raw) {
        var out = [], prev = null;
        (raw || []).forEach(function (l) {
            var text = clean(l.text);
            if (!text || isHalluc(text)) return;
            var key = text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
            if (prev && key === prev.key && l.s - prev.e < 1.5) { prev.e = Math.max(prev.e, l.e); return; }
            var line = { s: Math.max(0, +l.s || 0), e: Math.max(+l.s || 0, +l.e || 0), text: text, key: key };
            out.push(line); prev = line;
        });
        return out.map(function (l) { return { s: l.s, e: l.e, text: l.text }; });
    }

    /* ── экспорт ─────────────────────────────────────────────────────────── */
    function pad2(n) { return (n < 10 ? '0' : '') + n; }
    function lrcTime(t) {
        var m = Math.floor(t / 60), s = t - m * 60;
        return '[' + pad2(m) + ':' + (s < 10 ? '0' : '') + s.toFixed(2) + ']';
    }
    function srtTime(t) {
        var total = Math.max(0, Math.round(t * 1000));   // whole ms first: 3.9996 s is 00:00:04,000, not 00:00:03,1000
        var h = Math.floor(total / 3600000), m = Math.floor(total % 3600000 / 60000), s = Math.floor(total % 60000 / 1000), ms = total % 1000;
        return pad2(h) + ':' + pad2(m) + ':' + pad2(s) + ',' + String(ms).padStart(3, '0');
    }
    function toTxt(lines) { return lines.map(function (l) { return l.text; }).join('\n') + '\n'; }
    function toLrc(lines, meta) {
        var head = [];
        if (meta && meta.title) head.push('[ti:' + meta.title + ']');
        head.push('[re:Timbrica lyrics-transcription]');
        return head.concat(lines.map(function (l) { return lrcTime(l.s) + l.text; })).join('\n') + '\n';
    }
    function toSrt(lines) {
        return lines.map(function (l, i) {
            var end = Math.max(l.e, l.s + 1.2);
            return (i + 1) + '\n' + srtTime(l.s) + ' --> ' + srtTime(end) + '\n' + l.text + '\n';
        }).join('\n');
    }

    /* ── оркестрация ─────────────────────────────────────────────────────── */
    /*
     * opts: {
     *   lang: 'auto' | 'en' | 'ru' | …      язык песни
     *   model: 'fast' | 'accurate'          whisper base | small (ru → всегда GigaAM)
     *   separate: true, gate: true          gate = VAD только как детектор «есть ли пение»;
 *   cut: false                          cut = резать звук по VAD (стенд; в продукте выключено — вредит)
     *   slug, urls: { whisper, vad, gigaam: { worker, model[], mel[], vocab[] },
     *                 sensevoice: { worker, model[], tokens[], cmvn[] } }
     *   onStage(stage, frac, info)          'decode' | 'lid_model' | 'lang' | 'separate' | 'vad' | 'model' | 'transcribe' | 'post';
     *                                       у загрузки модели info = { loaded, total } в байтах (когда известны)
     *   onPlan(stages)                      этапы этого прогона по порядку — сразу и ещё раз, когда известен маршрут
     *   onRoute({lang, engine, separate})   маршрут, когда он стал известен (после определения языка)
     *   signal                              AbortSignal
     *   minSpeechSec                        меньше — считаем, что пения нет (по умолчанию 3)
     * }
     * Результат: { lines:[{s,e,text}], engine, model, lang, detectedLang,
     *              speechSec, totalSec, separated, gated, noVocals,
     *              engineFallback }   ← почему китайский лейн ушёл на whisper (код воркера)
     */
    function run(file, opts) {
        opts = opts || {};
        var stage = opts.onStage || function () {};
        var signal = opts.signal;
        var lang = (opts.lang || 'auto').toLowerCase().split('-')[0];
        var res = { engine: null, model: null, lang: lang, detectedLang: null, speechSec: 0, totalSec: 0, separated: false, gated: false, noVocals: false, lines: [] };

        function check() { if (aborted(signal)) throw abortError(); }

        /* Платная модель OpenAI (intl; замер и цена — docs/why/asr-openai.md): в
           браузере ни одной модели — только декодирование, распознаёт сервер. Поэтому
           ветка стоит ДО проверки модульных воркеров: старому браузеру этот путь
           годится. Звук — микс, куски по часам (content=music): Silero не слышит
           пения, а целую песню модель на 7 из 12 вернула пустой; кусками — 33 % WER
           против 64 % у whisper small на тех же 12 песнях. */
        if (opts.engine === 'openai' && global.OpenAiAsrLane && global.OpenAiAsrLane.available()) {
            if (opts.onPlan) { try { opts.onPlan(['decode', 'transcribe', 'post']); } catch (e) { /* noop */ } }
            stage('decode', 0);
            return decodeFile(file, signal).then(function (buf) {
                check();
                res.totalSec = buf.duration;
                if (opts.maxSec && buf.duration > opts.maxSec) {
                    var tl = new Error('too long: ' + Math.round(buf.duration) + 's > ' + opts.maxSec + 's');
                    tl.name = 'TooLongError'; tl.durationSec = buf.duration;
                    throw tl;
                }
                return toMono16k(buf);
            }).then(function (pcm) {
                check();
                stage('transcribe', 0);
                return global.OpenAiAsrLane.transcribe(pcm, SR, {
                    lang: lang, content: 'music', signal: signal,
                    onProgress: function (f) { stage('transcribe', f); },
                });
            }).then(function (out) {
                stage('post', 0);
                res.engine = 'openai'; res.model = 'gpt-transcribe';
                res.detectedLang = out.language || null;
                if (lang === 'auto' && out.language) res.lang = out.language;
                res.speechSec = res.totalSec;
                res.lines = postLines(piecesToLines(out.segments));
                stage('post', 1);
                return res;
            });
        }

        /* Все три движка распознавания грузят модель через import() ВНУТРИ воркера: whisper —
           transformers.js (ESM), GigaAM и SenseVoice — ORT 1.24, чья обвязка wasm тоже .mjs.
           Движок без модульных воркеров (Firefox < 114, Safari < 15) import() в воркере не умеет.
           Замер на эмуляции 2026-10-08: определение языка и распознавание — два воркера whisper,
           42 с повторов загрузки и «Что-то пошло не так. Попробуйте ещё раз или другой файл» — а
           файл ни при чём. Отказываем сразу, до декодирования; фразу говорит страница. */
        if (global.__moduleWorkerOk && !global.__moduleWorkerOk()) {
            var tooOld = new Error('module workers unsupported');
            tooOld.name = 'EngineTooOldError'; tooOld.code = 'MODULE_WORKER_UNSUPPORTED';
            return Promise.reject(tooOld);
        }

        /* Порядок и маршрут — по замерам 27–28.08 на корпусе реальных песен:
             • язык определяется ПЕРВЫМ и ПО МИКСУ: 9/9 против 8/9 по стему
               (сепаратор срезает часть фонетических признаков — индонезийская
               песня со стема уходила в яванский). Это ещё и дешевле: русской
               песне на «Быстрее» отделение не нужно вовсе, но узнать это можно
               только после определения языка;
             • ru → GigaAM всегда (whisper на русском пении заметно хуже);
             • zh / yue → SenseVoice всегда (замер 2026-09-18: 49.2 → 22.7 % на путунхуа,
               кантонский 45.8 → 15.0 %); ja / ko остаются на whisper — он там лучше;
             • отделение вокала стоит 5.9 п.п. (35.8 против 41.7 по миксу) и
               занимает больше половины времени — поэтому оно у «Точнее», а
               «Быстрее» идёт по миксу. */
        var gigaReady = !!(opts.urls && opts.urls.gigaam && global.AsrAdapter);
        var senseReady = !!(opts.urls && opts.urls.sensevoice && global.AsrAdapter && global.AsrAdapter.SENSEVOICE_LANGS);
        function isSenseLang(l) { return senseReady && !!global.AsrAdapter.SENSEVOICE_LANGS[l]; }
        var accurate = opts.model === 'accurate';
        var langWasAuto = lang === 'auto';
        var useGiga = lang === 'ru' && gigaReady;
        var useSense = isSenseLang(lang);
        var whisperModel = opts.whisperModel || (accurate ? 'small' : 'base');
        var sepReady = opts.separate !== false && !!global.MdxStemSeparator;
        /* Как узнаётся язык — от этого зависят и загрузки, и этапы полосы (17.10.2026):
             'auto' — «Авто»: base определяет язык ДО выбора движка, маршрут от него зависит;
             'same' — язык выбран, движок whisper: язык проверяет ТА ЖЕ модель, что потом
                      распознаёт, в том же воркере. До 17.10 сюда тоже качалась base
                      (77 МБ) только ради плашки «поют на другом языке», а потом ещё small;
             'none' — язык выбран, движок CTC (ru → GigaAM, zh/yue → SenseVoice): whisper
                      этому прогону не нужен вовсе. Чужую песню на русском лейне ловит доля
                      латиницы в выдаче (ниже) — так и было до 28.08, а расхождения при явном
                      выборе на проде — это «ru → en» (7 из 99 за 30 дней), латиница их и
                      видит. Китайская модель метит язык всегда одинаково — ей проверять нечем. */
        var lidMode = !(opts.urls && opts.urls.whisper) ? 'none' : langWasAuto ? 'auto' : (useGiga || useSense) ? 'none' : 'same';
        res.lidMode = lidMode;
        var lidStage = lidMode === 'auto' ? 'lid_model' : 'model';
        var mixPcm = null, whisperReady = false;
        function plan(r) { if (opts.onPlan) { try { opts.onPlan(stagesFor(r)); } catch (e) { /* noop */ } } }
        plan({ lid: lidMode, separate: !langWasAuto && accurate && sepReady && (useGiga || useSense),
               model: lidMode === 'same' ? 'ready' : 'load' });

        stage('decode', 0);
        return decodeFile(file, signal).then(function (buf) {
            check();
            res.totalSec = buf.duration;
            if (opts.maxSec && buf.duration > opts.maxSec) {
                var tl = new Error('too long: ' + Math.round(buf.duration) + 's > ' + opts.maxSec + 's');
                tl.name = 'TooLongError'; tl.durationSec = buf.duration;
                throw tl;
            }
            /* Язык определяем ПО МИКСУ и до отделения вокала:
                 • по миксу — 9/9 против 8/9 по стему (замер 28.08: сепаратор
                   срезает часть фонетических признаков);
                 • «Авто» — иначе whisper молча декодирует по-английски и
                   ПЕРЕВОДИТ вместо распознавания;
                 • выбран явно (whisper) — чтобы поймать чужой язык (жалоба 27.08: на
                   RU-странице английская песня считалась русской и выходила
                   как «Я тебя делаю» ×N);
                 • до отделения — чтобы не платить самым долгим этапом за
                   маршрут, который после определения языка может не понадобиться.
               «Авто» определяет на `base`: замер 27.08 даёт те же 8/9 языков, что и
               `small`, при вчетверо меньшем весе, а маршрут ещё неизвестен (русской
               песне потом нужен GigaAM, и small был бы скачан зря). */
            if (lidMode === 'none') return buf;
            return toMono16k(buf).then(function (pcm) {
                mixPcm = pcm;
                var lidModel = lidMode === 'auto' ? 'base' : whisperModel;
                /* WASM и здесь: 9/9 языков замерены именно на нём (node q8),
                   а fp16 на этом материале ненадёжен (см. блок про WebGPU выше). */
                stage(lidStage, 0);
                var lidT0 = Date.now();
                return detectLang(holdWhisper(opts.urls.whisper, lidModel), pcm, { modelSize: lidModel, forceWasm: true },
                                  opts.langChoices || null, signal, {
                    onModel: function (f, loaded, total) { stage(lidStage, f, { loaded: loaded, total: total }); },
                    onLid: function (f) { stage('lang', f); }
                }).then(function (lid) { res.lidMs = Date.now() - lidT0; return lid; });
            }).then(function (lid) {
                check();
                stage('lang', 1);
                if (lid) {
                    res.detectedLang = lid.lang; res.langTop = lid.top;
                    res.lidLoadMs = lid.loadMs; res.lidComputeMs = lid.computeMs;
                    if (lang === 'auto') lang = lid.lang;
                    /* Кантонского у whisper нет: на кантонской песне он отвечает «китайский»,
                       и это не расхождение с выбором человека, а потолок определителя. */
                    else if (lid.lang !== lang && !(lang === 'yue' && lid.lang === 'zh')) res.langMismatch = lid.lang;
                }
                if (lang === 'auto') lang = (opts.fallbackLang || 'en');   // LID не смог — говорим честно
                res.lang = lang;
                /* Русский → GigaAM. Но своя модель на 225 МБ на помеченной
                   «экономной» сети качается только по ЯВНОМУ выбору языка: за
                   нашу ДОГАДКУ пользователь трафиком платить не подписывался. */
                useGiga = lang === 'ru' && gigaReady && !(langWasAuto && isSaveData());
                /* То же правило и для китайской модели (237 МБ). */
                useSense = isSenseLang(lang) && !(langWasAuto && isSaveData());
                return buf;
            });
        }).then(function (buf) {
            check();
            /* ⚠️ Отделение вокала помогает НЕ ВСЕМ движкам, и это тоже было
               измерено на неверной фикстуре. Повтор на рабочей конфигурации
               (с таймкодами, штраф 1.15, 9 песен, whisper q8):

                   whisper small:  стем 50.2 %  ·  МИКС 47.7 %
                   whisper base:   стем 68.7 %  ·  МИКС 67.8 %

               Самому долгому этапу (150–290 с, больше половины прогона) whisper
               не радуется вовсе: разброс по языкам огромен в обе стороны
               (ko 95→53 за микс, ru 43→63 за стем), а в сумме микс не хуже.
               У GigaAM обратное и замерено отдельно: стем 31.0 против микса
               34.8 на шести русских песнях — там отделение остаётся.
               Итого: «Точнее» отделяет ТОЛЬКО на русском лейне; у whisper
               «Точнее» = модель покрупнее, и прогон стал вдвое короче.

               ⚠️ 2026-09-18: правило оказалось общим для CTC-движков, а не русским.
               SenseVoice на девяти китайских песнях: микс 24.4 % → стем 20.2 %
               (лучше или вровень на всех девяти). У CTC нет декодера, который «дочитывает» слово
               по контексту сквозь аккомпанемент, — ему чистый голос нужен, whisper
               нет. Поэтому условие — «CTC-лейн», а не «русский». */
            var useSep = sepReady && accurate && (useGiga || useSense);
            /* Маршрут известен только здесь (при «Авто» — после определения языка):
               странице он нужен, чтобы полоса прогресса знала свои этапы. */
            if (opts.onRoute) {
                try { opts.onRoute({ lang: lang, engine: useGiga ? 'gigaam' : useSense ? 'sensevoice' : 'whisper', separate: useSep }); } catch (e) { /* noop */ }
            }
            /* Модель распознавания уже в воркере, если язык проверяла она же
               ('same') или «Авто» определил на base и «Быстрее» распознаёт base. */
            whisperReady = !useGiga && !useSense && !!held && !held.dead && held.model === whisperModel;
            plan({ lid: lidMode, separate: useSep, model: whisperReady ? 'ready' : 'load' });
            if (!useSep) return mixPcm || toMono16k(buf);
            stage('separate', 0);
            return global.MdxStemSeparator.split(buf, {
                slug: opts.slug || 'lyrics-transcription',
                signal: signal,
                onProgress: function (p) { stage('separate', Math.max(0, Math.min(1, +p || 0))); }
            }).then(function (stems) {
                res.separated = true;
                return toMono16k(stems.vocal);
            }, function (err) {
                /* Only the user's cancel stops here — by THIS run's signal, not by
                   «abort|cancel» in a message (a separator dying with «Aborted()»
                   skipped the fallback to the mix and failed the whole run). */
                if (window.__isUserCancel ? window.__isUserCancel(err, signal) : aborted(signal)) throw err;
                /* сепаратор не смог (память, WebGPU, сеть) — идём по миксу,
                   честно помечая это в результате */
                res.separateError = String((err && err.message) || err).slice(0, 120);
                return toMono16k(buf);
            });
        }).then(function (pcm) {
            check();
            if (opts.gate === false || !res.separated || !opts.urls || !opts.urls.vad) return { pcm: pcm, map: null };   // по миксу Silero мёртв — детектор только по стему
            /* VAD по стему — только чтобы отличить инструментал от песни. Резать
               по нему звук НЕЛЬЗЯ: на 9 реальных песнях гейт 0.35 поднял WER
               whisper-small с 40 до 51 % (слов 0.68 от эталона — Silero не
               считает тянутые гласные речью), а от петель спасает не гейт, а
               анти-повторы декодера в whisper-worker.js (без них small — 113 %,
               с ними — 40 %). opts.cut === true оставляет старое поведение для
               стенда. */
            stage('vad', 0);
            return runVad(pcm, opts.urls.vad, { threshold: opts.vadThreshold || 0.35, minSpeechMs: 300, minSilenceMs: 300, paddingMs: 200 },
                function (p) { stage('vad', p); }, signal).then(function (vad) {
                check();
                if (!vad || !vad.segments) return { pcm: pcm, map: null };   // VAD отказал — считаем, что пение есть
                var minSpeech = opts.minSpeechSec != null ? opts.minSpeechSec : 3;
                res.speechSec = vad.totalSpeechSec || 0;
                if (res.speechSec < minSpeech) { res.noVocals = true; return null; }
                if (!opts.cut) return { pcm: pcm, map: null };
                var g = buildGate(pcm, vad.segments, {});
                res.gated = true;
                res.speechSec = g.speechSec;
                return { pcm: g.pcm, map: g.map };
            });
        }).then(function (gated) {
            /* Пения нет — распознавать нечего. Здесь стоял `return res`, и следующий шаг
               принимал объект результата за список строк: postLines(res) →
               «(t || []).forEach is not a function» вместо экрана «пения нет» — 64 ошибки
               за 30 дней до 17.10.2026, все на этапе «Собираю строки». */
            if (!gated) return null;
            check();
            var pcm = gated.pcm, map = gated.map;
            var hooks = {
                onModel: function (p, loaded, total) { stage('model', p, { loaded: loaded, total: total }); },
                onProgress: function (p) { stage('transcribe', p); },
                onNote: function (kind, m) { if (kind === 'model_downgraded') res.model = m.to; }
            };
            function runGigaam() {
                /* Модель определения языка больше не нужна — освобождаем память
                   ДО загрузки GigaAM (225 МБ): на телефоне два набора весов
                   разом это лишний риск нехватки памяти. */
                dropWhisper();
                stage('model', 0);
                res.engine = 'gigaam'; res.model = 'gigaam-v3-ctc-int8';
                res.lang = 'ru';
                return ctcRun('ru', { urls: opts.urls.gigaam }, pcm, hooks, signal).then(function (out) {
                    var lines = wordsToLines((out && out.words) || [], {}).map(function (l) {
                        return { s: origTime(map, l.s), e: origTime(map, l.e), text: l.text };
                    });
                    /* Язык здесь ВЫБРАН пользователем, а не определён — писать
                       «Язык: Русский» было бы враньём (так и было до 27.08).
                       Зато видно, когда песня не русская: помечаем и предлагаем
                       определить язык автоматически. */
                    if (latinShare(lines.map(function (l) { return l.text; }).join(' ')) > FOREIGN_LATIN_SHARE) {
                        res.langMismatch = 'auto';
                    }
                    return lines;
                });
            }
            /* Китайский лейн. Единицы — отдельные иероглифы со своим временем; строка
               рвётся на паузе или по длине (CJK_LINE). Письменность модели — упрощённая
               при любом диалекте: страница приводит её к той, что читает человек. */
            function runSense() {
                dropWhisper();
                stage('model', 0);
                res.engine = 'sensevoice'; res.model = 'sensevoice-small-yue-int8';
                return ctcRun(lang, { urls: { sensevoice: opts.urls.sensevoice } }, pcm, hooks, signal).then(function (out) {
                    return wordsToLines((out && out.words) || [], CJK_LINE).map(function (l) {
                        return { s: origTime(map, l.s), e: origTime(map, l.e), text: l.text };
                    });
                });
            }
            function runWhisper() {
                res.engine = 'whisper';
                /* Переключатель — это размер модели (см. комментарий в блейде):
                   «Быстрее» — base, «Точнее» — small; оба по миксу. */
                res.model = whisperModel;
                /* Воркер с ЭТОЙ моделью, если она уже загружена (язык проверяла она же,
                   или «Авто» определил на base, а распознаёт тоже base), — тот же: ни
                   второй загрузки весов, ни второго выделения памяти. С ДРУГОЙ моделью —
                   свежий воркер, как было: смену модели внутри воркера (base → small) мы
                   не мерили, и в этом месте 28.08 уже стоял вырожденный вывод (тогда
                   виновником оказался fp16 на WebGPU, а не воркер, — но гонять проверенное
                   не на той конфигурации незачем). */
                var h = holdWhisper(opts.urls.whisper, res.model);
                if (!whisperReady) stage('model', 0);
                var params = { modelSize: res.model, vadUsed: !!map, language: WHISPER_LANG[lang] || 'english' };
                return whisperRun(h, pcm, params, hooks, signal).then(function (out) {
                    var chunks = (out && out.chunks) || [];
                    if (!chunks.length && out && out.text) chunks = [{ text: out.text, timestamp: [0, pcm.length / SR] }];
                    return chunksToLines(chunks, map);
                });
            }

            if (useGiga) return runGigaam();
            if (useSense) {
                return runSense().catch(function (err) {
                    if (window.__isUserCancel ? window.__isUserCancel(err, signal) : aborted(signal)) throw err;
                    /* Модель не скачалась / не поместилась в память. Отказать целиком —
                       хуже, чем отдать whisper: на китайском он слабее вдвое, но не пуст
                       (и на кантонском не хуже, чем на путунхуа: 45.8 против 49.2 %).
                       Причину несём в результате — страница говорит её вслух. */
                    res.engineFallback = String((err && (err.pcCode || err.message)) || 'failed').slice(0, 60);
                    try { console.warn('[lyrics] SenseVoice lane failed, falling back to whisper:', err); } catch (e) { /* noop */ }
                    return runWhisper();
                });
            }
            return runWhisper();
        }).then(function (rawLines) {
            if (!rawLines) return res;
            stage('post', 0);
            res.lines = postLines(rawLines);
            stage('post', 1);
            return res;
        });
    }

    /* Этапы полосы по маршруту — здесь, а не в странице: маршрут знает конвейер.
       r.lid: 'auto' | 'same' | 'none' (см. lidMode в run); r.model: 'load' — модель
       распознавания ещё качать, 'ready' — она уже в воркере. */
    function stagesFor(r) {
        var s = ['decode'];
        if (r.lid === 'auto') s.push('lid_model', 'lang');
        else if (r.lid === 'same') s.push('model', 'lang');
        if (r.separate) s.push('separate', 'vad');
        if (r.model === 'load' && s.indexOf('model') < 0) s.push('model');
        s.push('transcribe', 'post');
        return s;
    }

    global.LyricsPipeline = {
        run: run, toTxt: toTxt, toLrc: toLrc, toSrt: toSrt,
        _internal: { buildGate: buildGate, origTime: origTime, postLines: postLines, piecesToLines: piecesToLines, wordsToLines: wordsToLines, clean: clean, isHalluc: isHalluc, latinShare: latinShare, chunksToLines: chunksToLines, stagesFor: stagesFor, WHISPER_LANG: WHISPER_LANG, CJK_LINE: CJK_LINE }
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = global.LyricsPipeline;
})(typeof window !== 'undefined' ? window : globalThis);
